import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal } from 'react-native';

/**
 * Componente che mostra un banner per installare l'app come PWA
 * - Su Android/Chrome: usa il prompt nativo
 * - Su iOS/Safari: mostra istruzioni manuali
 */
const InstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIOS, setIsIOS] = useState(false);
    const [showIOSInstructions, setShowIOSInstructions] = useState(false);

    useEffect(() => {
        // Rileva se siamo su iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // Non mostrare se siamo in modalità standalone (app già installata)
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
            return;
        }

        // Controlla se abbiamo già mostrato il prompt in questa sessione
        const promptShown = sessionStorage.getItem('install_prompt_shown');
        if (promptShown) {
            return;
        }

        // Su iOS, mostra sempre il prompt (Safari non ha beforeinstallprompt)
        if (isIOSDevice) {
            setTimeout(() => setShowPrompt(true), 3000); // Mostra dopo 3 secondi
            return;
        }

        // Controlla se c'è già un prompt memorizzato (Android/Chrome)
        if (window.deferredPrompt) {
            setDeferredPrompt(window.deferredPrompt);
            setShowPrompt(true);
        }

        // Ascolta l'evento beforeinstallprompt
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            window.deferredPrompt = e;
            setShowPrompt(true);
        };

        // Ascolta quando l'app viene installata
        const handleAppInstalled = () => {
            setShowPrompt(false);
            setDeferredPrompt(null);
            window.deferredPrompt = null;
            sessionStorage.setItem('install_prompt_shown', 'true');
            console.log('App installata con successo!');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (isIOS) {
            // Mostra istruzioni per iOS
            setShowIOSInstructions(true);
            return;
        }

        if (!deferredPrompt) return;

        // Mostra il prompt di installazione del browser
        deferredPrompt.prompt();

        // Aspetta la risposta dell'utente
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`Utente ha scelto: ${outcome}`);

        // Resetta il prompt
        setDeferredPrompt(null);
        window.deferredPrompt = null;
        setShowPrompt(false);
        sessionStorage.setItem('install_prompt_shown', 'true');
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        sessionStorage.setItem('install_prompt_shown', 'true');
    };

    if (!showPrompt) return null;

    return (
        <>
            <View style={styles.container}>
                <View style={styles.content}>
                    <Text style={styles.icon}>📲</Text>
                    <View style={styles.textContainer}>
                        <Text style={styles.title}>Installa 123 Scherma!</Text>
                        <Text style={styles.subtitle}>
                            {isIOS ? 'Aggiungi alla tua Home' : 'Aggiungi alla home per un accesso rapido'}
                        </Text>
                    </View>
                    <TouchableOpacity style={styles.installButton} onPress={handleInstallClick}>
                        <Text style={styles.installButtonText}>{isIOS ? 'Come fare' : 'Installa'}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.dismissButton} onPress={handleDismiss}>
                        <Text style={styles.dismissButtonText}>✕</Text>
                    </TouchableOpacity>
                </View>
            </View>

            {/* Modal con istruzioni per iOS */}
            <Modal
                visible={showIOSInstructions}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowIOSInstructions(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>📱 Installa su iPhone/iPad</Text>
                        <Text style={styles.modalText}>
                            1. Tocca l'icona{' '}
                            <Text style={styles.shareIcon}>⬆️</Text>
                            {' '}(Condividi) in basso
                        </Text>
                        <Text style={styles.modalText}>
                            2. Scorri e tocca "Aggiungi alla schermata Home"
                        </Text>
                        <Text style={styles.modalText}>
                            3. Conferma toccando "Aggiungi"
                        </Text>
                        <TouchableOpacity
                            style={styles.modalButton}
                            onPress={() => {
                                setShowIOSInstructions(false);
                                setShowPrompt(false);
                                sessionStorage.setItem('install_prompt_shown', 'true');
                            }}
                        >
                            <Text style={styles.modalButtonText}>Ho capito!</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'fixed',
        bottom: 70,
        left: 0,
        right: 0,
        zIndex: 9998,
        padding: 12,
    },
    content: {
        backgroundColor: '#1976d2',
        borderRadius: 12,
        padding: 12,
        flexDirection: 'row',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
        maxWidth: 500,
        marginHorizontal: 'auto',
    },
    icon: {
        fontSize: 28,
        marginRight: 12,
    },
    textContainer: {
        flex: 1,
    },
    title: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 16,
    },
    subtitle: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 13,
        marginTop: 2,
    },
    installButton: {
        backgroundColor: '#fff',
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 20,
        marginLeft: 12,
    },
    installButtonText: {
        color: '#1976d2',
        fontWeight: 'bold',
        fontSize: 14,
    },
    dismissButton: {
        marginLeft: 8,
        padding: 4,
    },
    dismissButtonText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 18,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: 16,
        padding: 24,
        width: '90%',
        maxWidth: 350,
    },
    modalTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 20,
        textAlign: 'center',
        color: '#1976d2',
    },
    modalText: {
        fontSize: 16,
        marginBottom: 12,
        lineHeight: 24,
        color: '#333',
    },
    shareIcon: {
        fontSize: 18,
    },
    modalButton: {
        backgroundColor: '#1976d2',
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 8,
        marginTop: 16,
        alignItems: 'center',
    },
    modalButtonText: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 16,
    },
});

export default InstallPrompt;
